import React, { useState } from 'react';
import { Button, Alert, Spin } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { useLoadShare } from '../Share/hooks/useLoadShare';
import { logger } from '@src/utils/misc/logger';
import { useNexaGateCheck, NexaGateState } from '../utils/nexaGateCheck';
import { checkCanUseNexa } from '@src/globalStore/profileStore';

const { baseUrl } = window.conviva;

interface ContinueConversationBannerProps {
  shareId: string;
}

export const ContinueConversationBanner: React.FC<ContinueConversationBannerProps> = ({
  shareId,
}) => {
  const navigate = useNavigate();
  const { loadShare, loading, error } = useLoadShare();
  const [isProcessing, setIsProcessing] = useState(false);
  const { state: gateState } = useNexaGateCheck();
  const canUseNexa = checkCanUseNexa();

  const handleContinue = async () => {
    setIsProcessing(true);
    const newSessionId = nanoid();

    try {
      await loadShare(shareId, newSessionId);
      logger.log('Session cloned successfully, redirecting', { newSessionId });
      navigate(`${baseUrl}/dashboard/nexa/${newSessionId}`);
    } catch (err) {
      logger.error('Failed to clone session', err);
      setIsProcessing(false);
    }
  };

  return (
    <div
      style={{
        padding: '24px',
        borderRadius: '8px',
        margin: '16px 0',
        textAlign: 'center',
      }}
    >
      {/* Show no permission message if user doesn't have access */}
      {(gateState === NexaGateState.BLOCKED ||
        gateState === NexaGateState.DISABLED ||
        !canUseNexa) && (
        <div style={{ fontSize: 16, color: '#666' }}>
          You do not have permission to use Nexa. Please contact your admin to enable Nexa for you.
        </div>
      )}

      {/* Show continue button only if user has access */}
      {gateState === NexaGateState.ALLOWED && canUseNexa && (
        <>
          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {loading || isProcessing ? (
            <div>
              <Spin size="large" />
              <div style={{ marginTop: 16, fontSize: 16, fontWeight: 500 }}>
                Preparing your session...
              </div>
            </div>
          ) : (
            <>
              <Button
                type="primary"
                size="large"
                icon={<ArrowRightOutlined />}
                onClick={handleContinue}
              >
                Continue Conversation
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
};
